import React, { useState, useCallback, useRef } from 'react';
import Button from '../../../ui/Button';
import { parseCSV } from '../utils';
import { CSV_TEMPLATE_COLUMNS, CSV_TEMPLATE_ROW } from '../constants';

export default function CsvUploadStep({ csvData, csvUsers, onUpdate, onNext }) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);

      if (parsed.errors.length > 0 && parsed.rows.length === 0) {
        setError(parsed.errors.join(', '));
        return;
      }

      setError(null);
      const users = parsed.rows.map(row => ({
        email: row.email,
        password: row.password,
        hostname: row.hostname || 'imap.titan.email',
        port: parseInt(row.port) || 993,
        use_ssl: row.use_ssl !== 'false',
        source_domain: row.source_domain || row.email?.split('@')[1],
        target_domain: row.target_domain || row.email?.split('@')[1],
        _valid: row._valid,
        _rowNumber: row._rowNumber
      }));

      onUpdate(parsed, users);
    };
    reader.readAsText(file);
  }, [onUpdate]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleDownloadTemplate = () => {
    const csv = CSV_TEMPLATE_COLUMNS.join(',') + '\n' + CSV_TEMPLATE_ROW.join(',');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'migration_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCellEdit = (rowIndex, field, value) => {
    if (!csvUsers) return;
    const updated = [...csvUsers];
    updated[rowIndex] = { ...updated[rowIndex], [field]: value };
    updated[rowIndex]._valid = !!(updated[rowIndex].email && updated[rowIndex].password);
    onUpdate(csvData, updated);
    setEditingCell(null);
  };

  const validCount = csvUsers?.filter(u => u._valid).length || 0;
  const totalCount = csvUsers?.length || 0;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload CSV File</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Upload a CSV with user credentials for bulk migration
        </p>
      </div>

      {/* Template Download */}
      <button
        onClick={handleDownloadTemplate}
        className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Download CSV Template
      </button>

      {/* Upload Area */}
      {!csvUsers?.length && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
            dragActive
              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
              : 'border-gray-300 dark:border-gray-700 hover:border-orange-400'
          }`}
        >
          <svg className="w-10 h-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Drop your CSV file here or click to browse
          </p>
          <p className="text-xs text-gray-400 mt-1">Supports .csv files</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={(e) => handleFile(e.target.files[0])}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Preview Table */}
      {csvUsers && csvUsers.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {validCount}/{totalCount} users valid
              </span>
              {csvData?.errors?.length > 0 && (
                <span className="text-sm text-red-500">{csvData.errors.length} errors</span>
              )}
            </div>
            <button
              onClick={() => { onUpdate(null, []); setError(null); }}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Clear & re-upload
            </button>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hostname</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Port</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Target Domain</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {csvUsers.map((user, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 text-gray-400">{user._rowNumber || idx + 1}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-white font-mono text-xs">
                      {editingCell?.row === idx && editingCell?.field === 'email' ? (
                        <input
                          autoFocus
                          defaultValue={user.email}
                          onBlur={(e) => handleCellEdit(idx, 'email', e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleCellEdit(idx, 'email', e.target.value)}
                          className="w-full px-1 py-0.5 border rounded text-xs"
                        />
                      ) : (
                        <span onDoubleClick={() => setEditingCell({ row: idx, field: 'email' })} className="cursor-text">
                          {user.email || <span className="text-red-400">Missing</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{user.hostname}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{user.port}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{user.target_domain}</td>
                    <td className="px-3 py-2 text-center">
                      {user._valid ? (
                        <svg className="w-4 h-4 text-emerald-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {validCount > 0 && (
            <Button variant="primary" onClick={onNext}>
              Continue with {validCount} user{validCount !== 1 ? 's' : ''}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
